import {cn} from "@/lib/utils";

import {DAR_SEVERITY_LABELS, DAR_SEVERITY_ORDER} from "../lib";
import type {DarsKpi} from "../types";
import {DeltaPill} from "./delta-pill";
import {KpiCard, type KpiCardDensity} from "./kpi-card";

type KpiCardDarsProps = {
  kpi: DarsKpi;
  icon?: React.ElementType;
  density?: KpiCardDensity;
  selected?: boolean;
  onSelect?: () => void;
};

const SEVERITY_DOT_CLASS: Record<"red" | "yellow" | "green", string> = {
  red: "bg-destructive",
  yellow: "bg-amber-400",
  green: "bg-emerald-500",
};

const SEVERITY_BAR_CLASS: Record<"red" | "yellow" | "green", string> = {
  red: "bg-destructive",
  yellow: "bg-amber-400",
  green: "bg-emerald-500",
};

/**
 * DAR tile with primary count plus secondary breakdown by severity. The
 * severity row uses Red > Yellow > Green priority emphasis: the row is
 * always rendered in that order so users scan toward severe items first.
 */
export function KpiCardDars({
  kpi,
  icon,
  density = "default",
  selected,
  onSelect,
}: KpiCardDarsProps) {
  const secondary = kpi.secondary ?? {red: 0, yellow: 0, green: 0};
  const isCompact = density === "compact";
  const severityTotal = DAR_SEVERITY_ORDER.reduce(
    (total, severity) => total + secondary[severity],
    0,
  );

  return (
    <KpiCard
      label={kpi.label ?? "DARs"}
      icon={icon}
      density={density}
      selected={selected}
      onSelect={onSelect}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={cn(
            "text-foreground min-w-0 font-bold tracking-tight tabular-nums",
            isCompact ? "text-3xl" : "text-4xl",
          )}
        >
          {kpi.current.toLocaleString()}
        </span>
        <DeltaPill value={kpi.delta_pct} size={isCompact ? "sm" : "default"} className="shrink-0" />
      </div>
      <p className="text-muted-foreground text-sm leading-snug">
        from <span className="tabular-nums">{kpi.previous.toLocaleString()}</span> last period
      </p>

      <div className={cn("mt-auto flex flex-col gap-1.5", !isCompact && "mt-1")}>
        <div
          className="bg-muted flex h-1.5 overflow-hidden rounded-full"
          aria-label="DAR severity distribution"
        >
          {DAR_SEVERITY_ORDER.map((severity) => {
            const pct = severityTotal > 0 ? (secondary[severity] / severityTotal) * 100 : 0;
            return (
              <span
                key={severity}
                className={SEVERITY_BAR_CLASS[severity]}
                style={{width: `${pct}%`}}
              />
            );
          })}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {DAR_SEVERITY_ORDER.map((severity) => (
            <span
              key={severity}
              className="text-muted-foreground flex items-center gap-1 text-[11px] leading-none"
            >
              <span
                className={cn(
                  "inline-block rounded-full",
                  isCompact ? "size-1.5" : "size-2",
                  SEVERITY_DOT_CLASS[severity],
                )}
              />
              <span>{DAR_SEVERITY_LABELS[severity]}</span>
              <span className="text-foreground font-semibold tabular-nums">
                {secondary[severity].toLocaleString()}
              </span>
            </span>
          ))}
        </div>
      </div>
    </KpiCard>
  );
}
